import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleAlert, CircleCheck, Download, Send } from 'lucide-react';
import Spinner from '@dashboard/components/Spinner';
import EmptyState from '@dashboard/components/EmptyState';
import StatusBadge from '@dashboard/components/StatusBadge';
import { DataGrid } from '@dashboard/components/DataGrid';
import { Alert, AlertDescription } from '@dashboard/components/ui/alert';
import { Badge } from '@dashboard/components/ui/badge';
import { Button as UiButton } from '@dashboard/components/ui/button';
import TicketCode from '@dashboard/components/TicketCode';
import {
  EditGuestDialog,
  OrderCard,
  OrderCardMenu,
  OrderGuestRemindersCard,
  SkeletonCard,
  SkeletonTableRows,
} from '@dashboard/components/OrderPage';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { toast } from '@dashboard/context/ToastContext';
import { chargeEmailLogsKey, chargeGuestsKey, chargeTicketsKey } from '@dashboard/lib/queryKeys';
import { formatDateTime } from '@dashboard/lib/format-date';
import { usePageRecordHref } from '@dashboard/lib/detailLinks';
import { fetcher } from '@dashboard/services/request';
import getPriceLabel from '@dashboard/services/translation/getPriceLabel';
import isAbandonedAbandonment from '@dashboard/services/abandonment/isAbandonedAbandonment';
import {
  CancelChargeFormDialog,
  cancelOffReason,
} from '@dashboard/components/OrderPage/CancelChargeDialog';
import useBookingGuests, { bookingTicketCount } from '../useBookingGuests';
import useBookingGuestActions from '../useBookingGuestActions';
import { bookingBackState } from '../bookingBack';
import { GUEST_REMINDER_LOG_TYPE } from '@dashboard/components/EmailLogColumns';

const muted = 'text-sm text-gray-500 dark:text-muted-foreground';

// Ticket PDFs exist for these older events and for every booking since PDFs shipped.
const PDF_PAGES = [
  '681aac984e3f8ba880f636f8',
  '667211f37afaf75caf59ffce',
  '67ff336a0ea017badff86621',
  '67e9ebab28eae4f818011f12',
];
const hasTicketPdf = payment =>
  PDF_PAGES.includes(payment.page?._id) ||
  (payment?.createdAt && new Date(payment.createdAt) > new Date('2025-05-12T08:35:07.000Z'));

// An optional booking's customer isn't asked, so an unfilled attendee is not missing anything.
export function detailsBadges(guest, optional) {
  if (!guest) return [];
  const badges = [];
  if (!guest.complete && !optional) badges.push({ status: 'unpaid', label: 'Missing details' });
  if (guest.flagged && !guest.reviewedAt)
    badges.push({ status: 'unpaid', label: 'Review details' });
  if (!badges.length) {
    badges.push({ status: 'hidden', label: guest.complete ? 'Complete' : 'Optional' });
  }
  return badges;
}

/**
 * Tickets without an attendee row follow, oldest first. The ticket list holds live tickets only, so
 * a test booking's attendees show without one.
 */
export function attendeeRows(tickets = [], guests = null) {
  const byId = new Map(tickets.map(ticket => [String(ticket._id), ticket]));
  const rows = (guests?.rows ?? []).map(guest => {
    const ticket = guest.ticketId ? byId.get(String(guest.ticketId)) : undefined;
    if (ticket) byId.delete(String(ticket._id));
    return { ticket: ticket ?? {}, guest };
  });
  const rest = [...byId.values()]
    .sort((a, b) => String(a._id).localeCompare(String(b._id)))
    .map(ticket => ({ ticket, guest: null }));
  return [...rows, ...rest];
}

const formatStatus = status =>
  status === 'PAID' ? 'Valid' : status === 'CANCELED' ? 'Canceled' : status;

const PaymentsBookingsDetailAttendees = ({ payment }) => {
  const { sellerId, token } = useAuth();
  const history = useHistory();
  const location = useLocation();
  const recordHref = usePageRecordHref();
  const queryClient = useQueryClient();
  const [editingGuest, setEditingGuest] = useState(null);
  const [cancelingTicket, setCancelingTicket] = useState(null);
  const cancelOff = cancelOffReason(payment, 'booking');
  const backState = bookingBackState(payment, location);

  const { data, isLoading, isError } = useQuery({
    queryKey: chargeTicketsKey(payment._id),
    queryFn: () => fetcher(`/sellers/${sellerId}/tickets/${payment._id}`, token),
    enabled: !!payment?._id,
  });
  const { data: guests, isLoading: isLoadingGuests } = useBookingGuests(payment);
  const { saveGuest, askToReview, reviewDialog, isReviewingGuest } = useBookingGuestActions(
    payment._id,
  );

  // On an event that collects attendee details a ticket is its attendee's, so an unnamed one reads
  // Not named yet; on one that never asks, it stays its holder's until the merchant adds someone.
  const tracksAttendees = Boolean(payment.attendeeStatus) && guests?.collects !== false;
  const optional = Boolean(payment.attendeesOptional || guests?.optional);

  const { mutate: downloadTicketPDF, isPending: isDownloadingTicketPDF } = useMutation({
    mutationFn: () => fetcher(`/sellers/${sellerId}/tickets/${payment._id}/pdf`, token),
    onSuccess: result => {
      if (result.pdfUrl == null) {
        toast({ title: 'Unable to download ticket PDF', type: 'error' });
        return;
      }
      const link = document.createElement('a');
      link.href = result.pdfUrl;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    onError: () => toast({ title: 'Failed to download ticket PDF', type: 'error' }),
  });

  const { mutate: remindBooker, isPending: isRemindingBooker } = useMutation({
    mutationFn: () =>
      fetcher(`/sellers/${sellerId}/charges/${payment._id}/guests/remind`, token, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: chargeGuestsKey(payment._id) });
      queryClient.invalidateQueries({ queryKey: chargeEmailLogsKey(payment._id) });
      toast({ title: 'Reminder sent' });
    },
    onError: error =>
      toast({ title: "Couldn't send the reminder", description: error?.message, type: 'error' }),
  });

  const tickets = data?.tickets ?? [];
  const rows = useMemo(() => attendeeRows(data?.tickets, guests), [data, guests]);

  const columns = useMemo(() => {
    const openAttendee = (ticket, state = {}) =>
      history.push(recordHref('attendees', ticket._id, payment.page), { ...backState, ...state });
    const rowActions = ({ ticket, guest }) => {
      if (!guest) {
        return [{ label: 'Add details', onSelect: () => openAttendee(ticket, { edit: true }) }];
      }
      const review = reviewed => ({
        label: reviewed ? 'Mark as reviewed' : 'Undo review',
        onSelect: () => askToReview(guest, reviewed),
        disabled: isReviewingGuest,
      });
      return [
        {
          label: guest.added ? 'Edit details' : 'Add details',
          onSelect: () => setEditingGuest(guest),
        },
        ...(guest.added && guest.flagged && !guest.reviewedAt ? [review(true)] : []),
        ...(guest.reviewedAt ? [review(false)] : []),
      ];
    };

    return [
      {
        id: 'attendee',
        header: 'Attendee',
        cell: ({ row }) => {
          const { ticket, guest } = row.original;
          const holder = { name: ticket.customerName, email: ticket.customerEmail };
          const attendee = guest?.added
            ? guest
            : ticket.attendee || !tracksAttendees
              ? holder
              : null;
          if (!attendee) return <div className={`${muted} whitespace-nowrap`}>Not named yet</div>;
          return (
            <div>
              <div className={ticket.status === 'CANCELED' ? muted : 'font-medium'}>
                {attendee.name || attendee.email}
              </div>
              {attendee.name && attendee.email ? (
                <div className={muted}>{attendee.email}</div>
              ) : null}
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'ticketType',
        header: 'Ticket',
        cell: ({ row }) => (
          <div className="overflow-hidden text-ellipsis whitespace-nowrap max-w-xs">
            {row.original.ticket.ticketType?.name || row.original.guest?.ticketTypeName || ''}
          </div>
        ),
        enableSorting: false,
      },
      ...(tracksAttendees
        ? [
            {
              id: 'guestDetails',
              header: 'Attendee details',
              cell: ({ row }) => (
                <div className="flex flex-wrap gap-1">
                  {detailsBadges(row.original.guest, optional).map(badge => (
                    <StatusBadge key={badge.label} {...badge} />
                  ))}
                </div>
              ),
              enableSorting: false,
            },
          ]
        : []),
      {
        id: 'ticketShortId',
        header: 'Ticket code',
        cell: ({ row }) => <TicketCode code={row.original.ticket.ticketShortId} />,
        enableSorting: false,
      },
      {
        id: 'price',
        header: 'Price',
        cell: ({ row }) => {
          const { _id, originalPrice, currency } = row.original.ticket;
          if (!_id) return '-';
          return (
            <span className="font-medium">
              {!originalPrice
                ? 'Free'
                : getPriceLabel({ amount: originalPrice, currency: currency || payment.currency })}
            </span>
          );
        },
        enableSorting: false,
      },
      {
        id: 'orderedAt',
        header: 'Booked on',
        cell: ({ row }) => {
          const { orderedAt } = row.original.ticket;
          return orderedAt ? (
            <span className="whitespace-nowrap">{formatDateTime(new Date(orderedAt))}</span>
          ) : (
            '-'
          );
        },
        enableSorting: false,
      },
      {
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const { status } = row.original.ticket;
          if (!status) return '-';
          return (
            <Badge
              variant={
                status === 'PAID' ? 'success' : status === 'CANCELED' ? 'destructive' : 'info'
              }
            >
              {formatStatus(status)}
            </Badge>
          );
        },
        enableSorting: false,
      },
      {
        id: 'checkedIn',
        header: 'Checked in',
        cell: ({ row }) => {
          const { ticket } = row.original;
          if (!ticket._id) return '-';
          if (ticket.status === 'CANCELED') return <Badge variant="warning">Void</Badge>;
          if (ticket.latestCheckIn?.status === 'CHECKEDIN') {
            return (
              <Badge variant="success">
                <CircleCheck className="h-3 w-3 mr-1" aria-hidden="true" />
                Checked in
              </Badge>
            );
          }
          return <Badge variant="info">Pending</Badge>;
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex justify-end items-center gap-2">
            {rowActions(row.original).map(action => (
              <UiButton
                key={action.label}
                variant="outline"
                size="sm"
                className="whitespace-nowrap"
                disabled={action.disabled || row.original.ticket.status === 'CANCELED'}
                onClick={action.onSelect}
              >
                {action.label}
              </UiButton>
            ))}
            {row.original.ticket._id ? (
              <div onClick={event => event.stopPropagation()}>
                <OrderCardMenu
                  label="Ticket options"
                  items={[
                    {
                      label: 'Cancel ticket',
                      destructive: true,
                      disabled: row.original.ticket.status === 'CANCELED' || Boolean(cancelOff),
                      onSelect: () => setCancelingTicket(row.original.ticket._id),
                    },
                  ]}
                />
              </div>
            ) : null}
          </div>
        ),
        enableSorting: false,
        meta: { className: 'text-right' },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps -- backState and history follow location
  }, [tracksAttendees, optional, payment, location, recordHref, isReviewingGuest, cancelOff]);

  const loadingGuests = Boolean(payment.attendeeStatus) && isLoadingGuests;

  return (
    <div className="w-full">
      {loadingGuests ? (
        <SkeletonCard titleWidth="w-40">
          <SkeletonTableRows count={2} />
        </SkeletonCard>
      ) : guests ? (
        <OrderGuestRemindersCard
          guests={guests}
          onOpenSent={() =>
            history.push({
              pathname: location.pathname.replace(/\/attendees\/?$/, '/emails'),
              search: `?type=${GUEST_REMINDER_LOG_TYPE}`,
            })
          }
          action={
            <UiButton
              variant="outline"
              size="sm"
              onClick={() => remindBooker()}
              disabled={!guests.editable || isRemindingBooker}
            >
              Send reminder now
              <Send />
            </UiButton>
          }
        />
      ) : null}
      <OrderCard
        title="Attendees"
        action={
          <div className="flex items-center gap-2">
            {hasTicketPdf(payment) && (
              <UiButton
                variant="outline"
                size="sm"
                onClick={() => downloadTicketPDF()}
                disabled={isAbandonedAbandonment(payment) || tickets.length === 0}
              >
                Download ticket PDF
                {isDownloadingTicketPDF ? <Spinner className="size-3.5" /> : <Download />}
              </UiButton>
            )}
          </div>
        }
      >
        {isError ? (
          <Alert variant="destructive" className="my-4">
            <CircleAlert />
            <AlertDescription>Failed to load tickets</AlertDescription>
          </Alert>
        ) : (
          <div className="mt-3">
            <DataGrid
              columns={columns}
              data={rows}
              isLoading={isLoading || loadingGuests}
              loadingRows={Math.min(bookingTicketCount(payment) || 1, 10)}
              getRowHref={({ ticket }) =>
                ticket._id ? recordHref('attendees', ticket._id, payment.page) : undefined
              }
              rowLinkState={backState}
              emptyState={
                <EmptyState>
                  <EmptyState.Subtitle>No tickets found for this booking.</EmptyState.Subtitle>
                </EmptyState>
              }
            />
          </div>
        )}
      </OrderCard>
      {guests ? (
        <EditGuestDialog
          guests={guests}
          row={editingGuest}
          open={Boolean(editingGuest)}
          onOpenChange={open => !open && setEditingGuest(null)}
          onSave={saveGuest}
        />
      ) : null}
      {reviewDialog}
      {cancelingTicket ? (
        <CancelChargeFormDialog
          payment={payment}
          noun="booking"
          open
          onOpenChange={next => !next && setCancelingTicket(null)}
          ticketId={cancelingTicket}
        />
      ) : null}
    </div>
  );
};

export default PaymentsBookingsDetailAttendees;
